import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, Bell, 
  Share2, Trash2, Edit, User, X, Check, Loader2, Paperclip, FileText, Image, Download, Eye,
  List, Pencil, CheckCircle, AlertCircle
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import InfoBanner from '../../components/ui/info-banner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkCalendar = ({ readOnly = false, enableHolidays = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [usersForShare, setUsersForShare] = useState([]);
  const [shareBranchName, setShareBranchName] = useState(null);
  
  // Hatırlatıcı states
  const [hatirlaticilar, setHatirlaticilar] = useState([]);
  const [hatirlaticiEvents, setHatirlaticiEvents] = useState([]);
  const [showHatirlaticiModal, setShowHatirlaticiModal] = useState(false);
  const [showHatirlaticiListModal, setShowHatirlaticiListModal] = useState(false);
  const [editingHatirlatici, setEditingHatirlatici] = useState(null);
  const [hatirlaticiForm, setHatirlaticiForm] = useState({
    baslik: '',
    aciklama: '',
    yillar: [],
    aylar: [],
    gunler: [],
    gun_tipi: 'belirli',
    hafta_gunleri: [],
    belgeler: [],
    renk: '#8B5CF6'
  });
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  // Resmi tatiller
  const [holidays, setHolidays] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ startDate: '', endDate: '', name: '' });
  const [holidayDayTypes, setHolidayDayTypes] = useState({}); // { 'YYYY-MM-DD': 'full'|'half' }
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [showHolidayListModal, setShowHolidayListModal] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [editHolidayData, setEditHolidayData] = useState({ name: '', day_type: 'full' });
  
  // Modal states
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  
  // Form state
  const [noteForm, setNoteForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    alarm_enabled: false,
    alarm_time: '',
    share_with_users: [],
    scheduled_share_time: '',
    color: 'blue',
    attachments: []
  });
  
  // File upload state
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Preview modal state
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const colorOptions = [
    { value: 'blue', label: 'Mavi', class: 'bg-blue-500' },
    { value: 'green', label: 'Yeşil', class: 'bg-green-500' },
    { value: 'red', label: 'Kırmızı', class: 'bg-red-500' },
    { value: 'yellow', label: 'Sarı', class: 'bg-yellow-500' },
    { value: 'purple', label: 'Mor', class: 'bg-purple-500' },
    { value: 'orange', label: 'Turuncu', class: 'bg-orange-500' },
    { value: 'pink', label: 'Pembe', class: 'bg-pink-500' },
    { value: 'cyan', label: 'Camgöbeği', class: 'bg-cyan-500' },
  ];

  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  
  const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  useEffect(() => {
    loadNotes();
    loadUsersForShare();
    loadHatirlaticilar();
    loadHolidays();
  }, [currentDate]);

  const loadHolidays = async () => {
    try {
      const year = currentDate.getFullYear();
      const res = await axios.get(`${API}/calendar/holidays`, {
        params: { year },
        headers: getAuthHeaders()
      });
      setHolidays(res.data.holidays || []);
    } catch (error) {
      console.error('Tatiller yüklenirken hata:', error);
    }
  };

  const openHolidayModal = (date = null) => {
    const iso = date ? formatDateToISO(date) : '';
    setHolidayForm({ startDate: iso, endDate: iso, name: '' });
    setHolidayDayTypes({});
    setShowHolidayModal(true);
  };

  // Başlangıç-bitiş aralığındaki tüm günleri (YYYY-MM-DD) döndürür
  const getHolidayRangeDates = () => {
    const { startDate, endDate } = holidayForm;
    if (!startDate) return [];
    const end = endDate && endDate >= startDate ? endDate : startDate;
    const arr = [];
    let cur = new Date(startDate + 'T00:00:00');
    const last = new Date(end + 'T00:00:00');
    let guard = 0;
    while (cur <= last && guard < 60) {
      arr.push(formatDateToISO(cur));
      cur.setDate(cur.getDate() + 1);
      guard += 1;
    }
    return arr;
  };

  const handleSaveHoliday = async () => {
    const rangeDates = getHolidayRangeDates();
    if (rangeDates.length === 0 || !holidayForm.name.trim()) {
      toast.error('Lütfen tarih(ler)i ve tatil adını girin');
      return;
    }
    setSavingHoliday(true);
    try {
      const days = rangeDates.map((d) => ({ date: d, day_type: holidayDayTypes[d] || 'full' }));
      await axios.post(`${API}/calendar/holidays`, { name: holidayForm.name.trim(), days }, { headers: getAuthHeaders() });
      toast.success(days.length > 1 ? `${days.length} günlük tatil kaydedildi` : 'Tatil kaydedildi');
      setShowHolidayModal(false);
      setHolidayForm({ startDate: '', endDate: '', name: '' });
      setHolidayDayTypes({});
      loadHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tatil kaydedilemedi');
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await axios.delete(`${API}/calendar/holidays/${id}`, { headers: getAuthHeaders() });
      toast.success('Tatil silindi');
      loadHolidays();
    } catch (error) {
      toast.error('Tatil silinemedi');
    }
  };

  const getHolidayForDate = (date) => {
    const iso = formatDateToISO(date);
    return holidays.find((h) => h.date === iso);
  };

  const startEditHoliday = (h) => {
    setEditingHolidayId(h.id);
    setEditHolidayData({ name: h.name, day_type: h.day_type || 'full' });
  };

  const saveEditHoliday = async (h) => {
    if (!editHolidayData.name.trim()) {
      toast.error('Tatil adı boş olamaz');
      return;
    }
    try {
      await axios.post(`${API}/calendar/holidays`, {
        name: editHolidayData.name.trim(),
        days: [{ date: h.date, day_type: editHolidayData.day_type }],
      }, { headers: getAuthHeaders() });
      toast.success('Tatil güncellendi');
      setEditingHolidayId(null);
      loadHolidays();
    } catch (error) {
      toast.error('Tatil güncellenemedi');
    }
  };


  const loadNotes = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const res = await axios.get(`${API}/calendar/notes`, {
        params: { year, month },
        headers: getAuthHeaders()
      });
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error('Notlar yüklenirken hata:', error);
      toast.error('Notlar yüklenemedi');
    }
    setLoading(false);
  };

  const loadHatirlaticilar = async () => {
    try {
      const [hatirlaticiRes, eventsRes] = await Promise.all([
        axios.get(`${API}/hr/hatirlaticilar`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hr/hatirlaticilar/takvim-etkinlikleri`, { headers: getAuthHeaders() })
      ]);
      setHatirlaticilar(hatirlaticiRes.data || []);
      setHatirlaticiEvents(eventsRes.data || []);
    } catch (error) {
      console.error('Hatırlatıcılar yüklenirken hata:', error);
    }
  };

  const loadUsersForShare = async () => {
    try {
      const res = await axios.get(`${API}/calendar/users-for-share`, {
        headers: getAuthHeaders()
      });
      setUsersForShare(res.data.users || []);
      setShareBranchName(res.data.branch_name || null);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // Not okunmamış mı kontrol et - paylaşılan notlar için read_by array'inde user.id olup olmadığına bak
  const isNoteUnread = (note) => {
    if (!note.is_shared) return false; // Kendi notları yeni değil
    if (!user?.id) return false;
    return !note.read_by?.includes(user.id);
  };

  // YENİ Badge - Not okundu olarak işaretle (mark-read API)
  const markNoteAsRead = async (noteId) => {
    try {
      await axios.post(`${API}/calendar/notes/${noteId}/mark-read`, {}, { headers: getAuthHeaders() });
      
      // State'i güncelle - notu okundu olarak işaretle
      setNotes(prev => prev.map(n => 
        n.id === noteId 
          ? { ...n, read_by: [...(n.read_by || []), user.id] }
          : n
      ));
      
      // Sidebar sayacını güncelle
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('Not okundu yapılırken hata:', error);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const openNewNoteModal = (date = null) => {
    const dateStr = date ? formatDateToISO(date) : formatDateToISO(new Date());
    setNoteForm({
      title: '',
      description: '',
      date: dateStr,
      time: '',
      alarm_enabled: false,
      alarm_time: '',
      share_with_users: [],
      scheduled_share_time: '',
      color: 'blue',
      attachments: []
    });
    setEditingNote(null);
    setShowNoteModal(true);
  };

  const openEditModal = (note) => {
    setNoteForm({
      title: note.title || '',
      description: note.description || '',
      date: note.date || '',
      time: note.time || '',
      alarm_enabled: note.alarm_enabled || false,
      alarm_time: note.alarm_time || '',
      share_with_users: note.share_with_users || [],
      scheduled_share_time: note.scheduled_share_time || '',
      color: note.color || 'blue',
      attachments: note.attachments || []
    });
    setEditingNote(note);
    setShowNoteModal(true);
  };

  const openViewModal = (note) => {
    setViewingNote(note);
    setShowViewModal(true);
  };
  
  // File upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz');
      return;
    }
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/calendar/notes/upload-attachment`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNoteForm(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), res.data]
      }));
      
      toast.success('Belge yüklendi');
    } catch (error) {
      console.error('Dosya yükleme hatası:', error);
      toast.error('Dosya yüklenemedi');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Remove attachment
  const removeAttachment = (attachmentId) => {
    setNoteForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== attachmentId)
    }));
  };
  
  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Download file
  const downloadFile = (attachment) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Preview file
  const openPreview = (attachment) => {
    setPreviewFile(attachment);
    setShowPreviewModal(true);
  };

  const formatDateToISO = (date) => {
    // Yerel saat dilimine göre tarih formatla (UTC dönüşümü ile 1 gün kaymasını önle)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSaveNote = async () => {
    if (!noteForm.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }

    try {
      if (editingNote) {
        await axios.put(`${API}/calendar/notes/${editingNote.id}`, noteForm, {
          headers: getAuthHeaders()
        });
        toast.success('Not güncellendi');
      } else {
        await axios.post(`${API}/calendar/notes`, noteForm, {
          headers: getAuthHeaders()
        });
        toast.success('Not oluşturuldu');
      }
      setShowNoteModal(false);
      loadNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Bu notu silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/calendar/notes/${noteId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Not silindi');
      setShowViewModal(false);
      loadNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  const toggleUserShare = (userId) => {
    setNoteForm(prev => {
      const users = prev.share_with_users.includes(userId)
        ? prev.share_with_users.filter(id => id !== userId)
        : [...prev.share_with_users, userId];
      return { ...prev, share_with_users: users };
    });
  };

  // Calendar helpers
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Pazartesi = 0
  };

  const getNotesForDate = (date) => {
    const dateStr = formatDateToISO(date);
    const dayNotes = notes.filter(n => n.date === dateStr);
    const dayReminders = hatirlaticiEvents.filter(h => h.tarih === dateStr);
    return [...dayNotes, ...dayReminders];
  };

  const getColorClass = (color) => {
    const opt = colorOptions.find(c => c.value === color);
    return opt ? opt.class : 'bg-blue-500';
  };

  // Hatırlatıcı helper arrays
  const aylar = [
    { value: 1, label: 'Ocak' },
    { value: 2, label: 'Şubat' },
    { value: 3, label: 'Mart' },
    { value: 4, label: 'Nisan' },
    { value: 5, label: 'Mayıs' },
    { value: 6, label: 'Haziran' },
    { value: 7, label: 'Temmuz' },
    { value: 8, label: 'Ağustos' },
    { value: 9, label: 'Eylül' },
    { value: 10, label: 'Ekim' },
    { value: 11, label: 'Kasım' },
    { value: 12, label: 'Aralık' }
  ];

  const haftaGunleri = [
    { value: 1, label: 'Pazartesi' },
    { value: 2, label: 'Salı' },
    { value: 3, label: 'Çarşamba' },
    { value: 4, label: 'Perşembe' },
    { value: 5, label: 'Cuma' },
    { value: 6, label: 'Cumartesi' },
    { value: 7, label: 'Pazar' }
  ];

  const renkSecenekleri = [
    { value: '#8B5CF6', label: 'Mor' },
    { value: '#3B82F6', label: 'Mavi' },
    { value: '#10B981', label: 'Yeşil' },
    { value: '#F59E0B', label: 'Turuncu' },
    { value: '#EF4444', label: 'Kırmızı' },
    { value: '#EC4899', label: 'Pembe' },
    { value: '#6366F1', label: 'İndigo' },
    { value: '#14B8A6', label: 'Teal' }
  ];

  const currentYear = new Date().getFullYear();
  const yilSecenekleri = [
    { value: 'her_yil', label: 'Her Yıl' },
    ...Array.from({ length: 5 }, (_, i) => ({ 
      value: String(currentYear + i), 
      label: String(currentYear + i) 
    }))
  ];

  const toggleArrayValue = (array, value) => {
    if (array.includes(value)) {
      return array.filter(v => v !== value);
    }
    return [...array, value];
  };

  // Hatırlatıcı functions
  const openHatirlaticiModal = (hatirlatici = null) => {
    if (hatirlatici) {
      setEditingHatirlatici(hatirlatici);
      setHatirlaticiForm({
        baslik: hatirlatici.baslik || '',
        aciklama: hatirlatici.aciklama || '',
        yillar: hatirlatici.yillar || [],
        aylar: hatirlatici.aylar || [],
        gunler: hatirlatici.gunler || [],
        gun_tipi: hatirlatici.gun_tipi || 'belirli',
        hafta_gunleri: hatirlatici.hafta_gunleri || [],
        belgeler: hatirlatici.belgeler || [],
        renk: hatirlatici.renk || '#8B5CF6'
      });
    } else {
      setEditingHatirlatici(null);
      setHatirlaticiForm({
        baslik: '',
        aciklama: '',
        yillar: [],
        aylar: ['her_ay'],
        gunler: [],
        gun_tipi: 'belirli',
        hafta_gunleri: [],
        belgeler: [],
        renk: '#8B5CF6'
      });
    }
    setShowHatirlaticiModal(true);
  };

  const handleHatirlaticiSubmit = async () => {
    if (!hatirlaticiForm.baslik) {
      toast.error('Başlık gerekli');
      return;
    }

    if (hatirlaticiForm.gun_tipi === 'belirli' && hatirlaticiForm.gunler.length === 0) {
      toast.error('En az bir gün seçin');
      return;
    }

    if (hatirlaticiForm.gun_tipi === 'haftalik' && hatirlaticiForm.hafta_gunleri.length === 0) {
      toast.error('En az bir hafta günü seçin');
      return;
    }

    try {
      const url = editingHatirlatici 
        ? `${API}/hr/hatirlaticilar/${editingHatirlatici.id}`
        : `${API}/hr/hatirlaticilar`;
      
      if (editingHatirlatici) {
        await axios.put(url, hatirlaticiForm, { headers: getAuthHeaders() });
        toast.success('Hatırlatıcı güncellendi');
      } else {
        await axios.post(url, hatirlaticiForm, { headers: getAuthHeaders() });
        toast.success('Hatırlatıcı oluşturuldu');
      }
      setShowHatirlaticiModal(false);
      loadHatirlaticilar();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteHatirlatici = async (id) => {
    if (!window.confirm('Bu hatırlatıcıyı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/hr/hatirlaticilar/${id}`, { headers: getAuthHeaders() });
      toast.success('Hatırlatıcı silindi');
      loadHatirlaticilar();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleToggleHatirlaticiComplete = async (hatirlaticiId, tarih) => {
    try {
      const res = await axios.put(
        `${API}/hr/hatirlaticilar/${hatirlaticiId}/toggle-complete`,
        { tarih },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.tamamlandi ? 'Hatırlatıcı tamamlandı' : 'Hatırlatıcı aktifleştirildi');
      loadHatirlaticilar();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const colorMap = {
    blue: '#3b82f6',
    green: '#22c55e',
    red: '#ef4444',
    yellow: '#eab308',
    purple: '#a855f7',
    orange: '#f97316',
    pink: '#ec4899',
    cyan: '#06b6d4',
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR');
  };

  // Build calendar grid
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const days = [];
    
    // Empty cells for days before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-28 border border-gray-100 bg-gray-50"></div>);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayNotes = getNotesForDate(date);
      const isToday = formatDateToISO(date) === formatDateToISO(new Date());
      const holiday = getHolidayForDate(date);
      
      days.push(
        <div
          key={day}
          className={`h-28 border p-1 cursor-pointer hover:bg-blue-50 transition-colors ${
            holiday ? 'bg-rose-50 border-rose-300' : isToday ? 'bg-blue-100 border-blue-400' : 'bg-white border-gray-200'
          }`}
          onClick={() => openNewNoteModal(date)}
        >
          <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
            {day}
          </div>
          {holiday && (
            <div
              className="group/holiday text-[10px] leading-tight font-medium px-1 py-0.5 rounded bg-rose-600 text-white mb-1 flex items-center justify-between gap-1"
              title={holiday.name}
              data-testid={`holiday-cell-${holiday.date}`}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="truncate">{holiday.name}{holiday.day_type === 'half' ? ' (Yarım)' : ''}</span>
              {enableHolidays && !readOnly && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDeleteHoliday(holiday.id); }}
                  className="opacity-0 group-hover/holiday:opacity-100 transition-opacity shrink-0"
                  title="Tatili sil"
                  data-testid={`holiday-delete-${holiday.date}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
          <div className="space-y-0.5 overflow-y-auto max-h-20">
            {dayNotes.slice(0, 3).map(note => (
              <div
                key={note.id}
                className={cn(
                  "text-xs px-1 py-0.5 rounded truncate cursor-pointer",
                  note.is_hatirlatici 
                    ? (note.tamamlandi ? "bg-gray-400 text-white" : "text-white")
                    : `text-white ${getColorClass(note.color)}`
                )}
                style={note.is_hatirlatici && !note.tamamlandi ? { backgroundColor: note.renk } : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (note.is_hatirlatici) {
                    handleToggleHatirlaticiComplete(note.hatirlatici_id, note.tarih);
                  } else {
                    openViewModal(note);
                  }
                }}
                title={note.is_hatirlatici ? note.baslik : note.title}
              >
                {note.is_hatirlatici ? (
                  <>
                    <Bell className="inline h-2.5 w-2.5 mr-1" />
                    {note.baslik}
                    {note.tamamlandi && <Check className="inline h-2.5 w-2.5 ml-1" />}
                  </>
                ) : (
                  <>
                    {note.time && <span className="mr-1">{note.time}</span>}
                    {note.title}
                    {note.is_shared && !note.is_birthday && <Share2 className="inline h-2.5 w-2.5 ml-1" />}
                  </>
                )}
              </div>
            ))}
            {dayNotes.length > 3 && (
              <div className="text-xs text-gray-500 pl-1">+{dayNotes.length - 3} daha</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="space-y-6" data-testid="work-calendar">
      {/* Bilgilendirme Notu */}
      <InfoBanner id="work-calendar-info" variant="info">
        <strong>Takvim:</strong> Takvimi kullanarak ajandanıza yeni notlar ekleyebilir, hatırlatıcı kurabilir ve bu notlara istediğiniz kişileri ekleyebilirsiniz. 
        <strong>Hatırlatıcı ekle</strong> ile daha karmaşık zaman aralıklarında hatırlatmalar yapabilirsiniz. 
        <strong>Hatırlatıcı listesinden</strong> bu hatırlatmalara ulaşabilirsiniz. 
        Sadece kendi oluşturduğunuz notları silebilirsiniz.
      </InfoBanner>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <CalendarIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Takvim / Ajanda</h1>
            <p className="text-gray-500">Notlarınızı ve etkinliklerinizi yönetin</p>
          </div>
        </div>
        <div className="flex gap-2">
          {enableHolidays && !readOnly && (
            <>
              <Button
                onClick={() => openHolidayModal()}
                className="gap-2 bg-rose-600 hover:bg-rose-700"
                data-testid="add-holiday-btn"
              >
                <CalendarIcon className="h-4 w-4" />
                Tatil Ekle
              </Button>
              <Button
                onClick={() => setShowHolidayListModal(true)}
                variant="outline"
                className="gap-2 border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="holiday-list-btn"
              >
                <CalendarIcon className="h-4 w-4" />
                Tatil Listesi {holidays.length > 0 ? `(${holidays.length})` : ''}
              </Button>
            </>
          )}
          <Button
            onClick={() => setShowHatirlaticiListModal(true)}
            variant="outline"
            className="gap-2"
            data-testid="reminder-list-btn"
          >
            <List className="h-4 w-4" />
            Hatırlatıcı Listesi
          </Button>
          <Button
            onClick={() => openHatirlaticiModal()}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
            data-testid="add-reminder-btn"
          >
            <Bell className="h-4 w-4" />
            Hatırlatıcı Ekle
          </Button>
          <Button onClick={() => openNewNoteModal()} className="gap-2" data-testid="new-note-btn">
            <Plus className="h-4 w-4" />
            Yeni Not
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday}>
                Bugün
              </Button>
            </div>
            <h2 className="text-xl font-bold text-gray-800">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="text-sm text-gray-500">
              {notes.length} not
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div>
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-1">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2 bg-gray-100">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {renderCalendar()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Notes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()} - Tüm Notlar
          </CardTitle>
          <p className="text-sm text-gray-500">Bu aydaki tüm notlarınız ve paylaşılan notlar</p>
        </CardHeader>
        <CardContent>
          {notes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Bu ayda henüz not bulunmuyor</p>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {notes.sort((a, b) => {
                const dateA = new Date(a.date + 'T' + (a.time || '00:00'));
                const dateB = new Date(b.date + 'T' + (b.time || '00:00'));
                return dateA - dateB;
              }).map((note) => {
                const isUnread = isNoteUnread(note);
                return (
                <div
                  key={note.id}
                  className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md ${
                    isUnread ? 'bg-blue-50 ring-2 ring-blue-300' : 
                    note.is_shared ? 'bg-purple-50 border-purple-400' : 'bg-white border-blue-400'
                  }`}
                  style={{ borderLeftColor: note.color ? colorMap[note.color] : undefined }}
                  onClick={() => {
                    if (isUnread) {
                      markNoteAsRead(note.id);
                    }
                    openViewModal(note);
                  }}
                  data-testid={`note-card-${note.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full`} style={{ backgroundColor: colorMap[note.color] || '#3b82f6' }}></span>
                        <h3 className={`font-semibold truncate ${isUnread ? 'text-blue-900' : 'text-gray-800'}`}>{note.title}</h3>
                        {isUnread && (
                          <Badge className="bg-red-500 text-white text-xs animate-pulse">YENİ</Badge>
                        )}
                        {note.is_shared && !note.is_birthday && (
                          <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-300">
                            <Share2 className="h-3 w-3 mr-1" />
                            Paylaşılan
                          </Badge>
                        )}
                        {note.is_birthday && (
                          <Badge variant="outline" className="text-xs bg-pink-100 text-pink-700 border-pink-300">
                            🎁 Doğum Günü
                          </Badge>
                        )}
                      </div>
                      {note.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{note.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {new Date(note.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                        </span>
                        {note.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {note.time}
                          </span>
                        )}
                        {note.alarm_enabled && (
                          <Bell className="h-3 w-3 text-orange-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {note.is_shared ? `Paylaşan: ${note.user_name}` : `Oluşturan: ${note.user_name}`}
                      </p>
                    </div>
                    {!note.is_shared && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNote(note.id);
                        }}
                        title="Notu Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note Create/Edit Modal */}
      <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNote ? 'Notu Düzenle' : 'Yeni Not Oluştur'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Başlık *</Label>
              <Input
                value={noteForm.title}
                onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
                placeholder="Not başlığı"
                data-testid="note-title"
              />
            </div>

            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={noteForm.description}
                onChange={(e) => setNoteForm({...noteForm, description: e.target.value})}
                placeholder="Detaylı açıklama"
                rows={3}
                data-testid="note-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={noteForm.date}
                  onChange={(e) => setNoteForm({...noteForm, date: e.target.value})}
                  data-testid="note-date"
                />
              </div>
              <div>
                <Label>Saat</Label>
                <Input
                  type="time"
                  value={noteForm.time}
                  onChange={(e) => setNoteForm({...noteForm, time: e.target.value})}
                  data-testid="note-time"
                />
              </div>
            </div>

            <div>
              <Label>Renk</Label>
              <div className="flex gap-2 mt-1">
                {colorOptions.map(color => (
                  <button
                    key={color.value}
                    className={`w-8 h-8 rounded-full ${color.class} ${
                      noteForm.color === color.value ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                    }`}
                    onClick={() => setNoteForm({...noteForm, color: color.value})}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            {/* Alarm */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-orange-500" />
                  <Label>Alarm / Hatırlatıcı</Label>
                </div>
                <Switch
                  checked={noteForm.alarm_enabled}
                  onCheckedChange={(checked) => setNoteForm({...noteForm, alarm_enabled: checked})}
                />
              </div>
              {noteForm.alarm_enabled && (
                <Input
                  type="datetime-local"
                  value={noteForm.alarm_time}
                  onChange={(e) => setNoteForm({...noteForm, alarm_time: e.target.value})}
                  placeholder="Alarm zamanı"
                />
              )}
            </div>

            {/* Share */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Share2 className="h-4 w-4 text-blue-500" />
                  <Label>Paylaşım</Label>
                </div>
                {shareBranchName && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {shareBranchName} şubesi
                  </span>
                )}
              </div>
              
              <div className="max-h-32 overflow-y-auto space-y-1">
                {usersForShare.map(u => (
                  <div
                    key={u.id}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                      noteForm.share_with_users.includes(u.id) ? 'bg-blue-100' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => toggleUserShare(u.id)}
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{u.name}</span>
                    </div>
                    {noteForm.share_with_users.includes(u.id) && (
                      <Check className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                ))}
                {usersForShare.length === 0 && (
                  <p className="text-sm text-gray-500">
                    {shareBranchName ? 'Bu şubede paylaşılacak kullanıcı yok' : 'Paylaşılacak kullanıcı yok'}
                  </p>
                )}
              </div>

              {noteForm.share_with_users.length > 0 && (
                <div>
                  <Label className="text-sm">Zamanlanmış Paylaşım (Opsiyonel)</Label>
                  <Input
                    type="datetime-local"
                    value={noteForm.scheduled_share_time}
                    onChange={(e) => setNoteForm({...noteForm, scheduled_share_time: e.target.value})}
                    placeholder="Paylaşım zamanı"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Belirtilen zamana kadar not paylaşılan kişilere görünmez
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Belge Ekleme */}
          <div className="border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-blue-500" />
                <Label>Belgeler</Label>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
              >
                {uploadingFile ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Belge Ekle
                  </>
                )}
              </Button>
            </div>
            
            {/* Yüklenen belgeler listesi */}
            {noteForm.attachments && noteForm.attachments.length > 0 && (
              <div className="space-y-2">
                {noteForm.attachments.map((attachment) => (
                  <div 
                    key={attachment.id} 
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {getFileIcon(attachment.type)}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{attachment.filename}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openPreview(attachment)}
                        title="Ön İzleme"
                      >
                        <Eye className="h-4 w-4 text-blue-500" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(attachment.id)}
                        title="Kaldır"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-xs text-gray-500">
              PDF, Word, Excel, resim dosyaları desteklenir. Maksimum 10MB.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteModal(false)}>İptal</Button>
            <Button onClick={handleSaveNote} data-testid="save-note-btn">
              {editingNote ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Note Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full ${getColorClass(viewingNote?.color)}`}></div>
              {viewingNote?.title}
              {viewingNote?.is_birthday && (
                <Badge variant="outline" className="ml-2 bg-pink-100 text-pink-700 border-pink-300">
                  🎁 Doğum Günü
                </Badge>
              )}
              {viewingNote?.is_shared && !viewingNote?.is_birthday && (
                <Badge variant="outline" className="ml-2">
                  <Share2 className="h-3 w-3 mr-1" />
                  Paylaşılan
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {viewingNote && (
            <div className="space-y-4">
              {viewingNote.description && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="text-gray-800 whitespace-pre-wrap">{viewingNote.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="text-gray-800">{viewingNote.date}</p>
                </div>
                {viewingNote.time && (
                  <div>
                    <Label className="text-gray-500">Saat</Label>
                    <p className="text-gray-800">{viewingNote.time}</p>
                  </div>
                )}
              </div>

              {viewingNote.alarm_enabled && viewingNote.alarm_time && (
                <div className="flex items-center gap-2 text-orange-600">
                  <Bell className="h-4 w-4" />
                  <span className="text-sm">Alarm: {formatDateTime(viewingNote.alarm_time)}</span>
                </div>
              )}

              <div className="border-t pt-3 text-xs text-gray-500">
                <p>Oluşturan: <strong>{viewingNote.user_name}</strong></p>
                <p>Oluşturulma: {formatDateTime(viewingNote.created_at)}</p>
                {viewingNote.updated_at !== viewingNote.created_at && (
                  <p>Güncelleme: {formatDateTime(viewingNote.updated_at)}</p>
                )}
              </div>
              
              {/* Belgeler - Görüntüleme */}
              {viewingNote.attachments && viewingNote.attachments.length > 0 && (
                <div className="border-t pt-3">
                  <Label className="text-gray-500 flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4" />
                    Ekli Belgeler ({viewingNote.attachments.length})
                  </Label>
                  <div className="space-y-2">
                    {viewingNote.attachments.map((attachment) => (
                      <div 
                        key={attachment.id} 
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md border"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {getFileIcon(attachment.type)}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{attachment.filename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => openPreview(attachment)}
                            title="Ön İzleme"
                          >
                            <Eye className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadFile(attachment)}
                            title="İndir"
                          >
                            <Download className="h-4 w-4 text-green-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {viewingNote && !viewingNote.is_shared && !viewingNote.is_birthday && (
              <>
                <Button variant="destructive" size="sm" onClick={() => handleDeleteNote(viewingNote.id)}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Sil
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  setShowViewModal(false);
                  openEditModal(viewingNote);
                }}>
                  <Edit className="h-4 w-4 mr-1" />
                  Düzenle
                </Button>
              </>
            )}
            <Button onClick={() => setShowViewModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewFile && getFileIcon(previewFile.type)}
              {previewFile?.filename}
            </DialogTitle>
          </DialogHeader>
          
          {previewFile && (
            <div className="flex-1 overflow-auto">
              {previewFile.type.startsWith('image/') ? (
                <img 
                  src={previewFile.url} 
                  alt={previewFile.filename}
                  className="max-w-full h-auto mx-auto"
                />
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-4">Bu dosya türü önizlenemiyor</p>
                  <Button onClick={() => downloadFile(previewFile)} className="gap-2">
                    <Download className="h-4 w-4" />
                    İndir
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {previewFile && (
              <Button variant="outline" onClick={() => downloadFile(previewFile)} className="gap-2">
                <Download className="h-4 w-4" />
                İndir
              </Button>
            )}
            <Button onClick={() => setShowPreviewModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatıcı Ekleme/Düzenleme Modalı */}
      <Dialog open={showHatirlaticiModal} onOpenChange={setShowHatirlaticiModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-purple-600" />
              {editingHatirlatici ? 'Hatırlatıcı Düzenle' : 'Yeni Hatırlatıcı'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Başlık ve Açıklama */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>İşin Adı / Başlık *</Label>
                <Input
                  value={hatirlaticiForm.baslik}
                  onChange={(e) => setHatirlaticiForm({...hatirlaticiForm, baslik: e.target.value})}
                  placeholder="Örn: Personel Değerlendirmesi, İzin Takibi..."
                  data-testid="reminder-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Açıklama / Not</Label>
                <Textarea
                  value={hatirlaticiForm.aciklama}
                  onChange={(e) => setHatirlaticiForm({...hatirlaticiForm, aciklama: e.target.value})}
                  placeholder="Detaylı açıklama yazın..."
                  rows={3}
                  data-testid="reminder-description-input"
                />
              </div>
            </div>

            {/* Renk Seçimi */}
            <div className="space-y-2">
              <Label>Renk</Label>
              <div className="flex gap-2 flex-wrap">
                {renkSecenekleri.map((renk) => (
                  <button
                    key={renk.value}
                    type="button"
                    onClick={() => setHatirlaticiForm({...hatirlaticiForm, renk: renk.value})}
                    className={cn(
                      "w-8 h-8 rounded-full border-2 transition-all",
                      hatirlaticiForm.renk === renk.value ? "border-gray-900 scale-110" : "border-transparent"
                    )}
                    style={{ backgroundColor: renk.value }}
                    title={renk.label}
                  />
                ))}
              </div>
            </div>

            {/* Yıl Seçimi */}
            <div className="space-y-2">
              <Label>Hangi Yıllarda?</Label>
              <div className="flex gap-2 flex-wrap">
                {yilSecenekleri.map((yil) => (
                  <Button
                    key={yil.value}
                    type="button"
                    variant={hatirlaticiForm.yillar.includes(yil.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      yillar: toggleArrayValue(hatirlaticiForm.yillar, yil.value)
                    })}
                  >
                    {yil.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Ay Seçimi */}
            <div className="space-y-2">
              <Label>Hangi Aylarda?</Label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant={hatirlaticiForm.aylar.includes('her_ay') ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHatirlaticiForm({
                    ...hatirlaticiForm, 
                    aylar: hatirlaticiForm.aylar.includes('her_ay') ? [] : ['her_ay']
                  })}
                >
                  Her Ay
                </Button>
                {aylar.map((ay) => (
                  <Button
                    key={ay.value}
                    type="button"
                    variant={hatirlaticiForm.aylar.includes(ay.value) ? "default" : "outline"}
                    size="sm"
                    disabled={hatirlaticiForm.aylar.includes('her_ay')}
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      aylar: toggleArrayValue(hatirlaticiForm.aylar.filter(a => a !== 'her_ay'), ay.value)
                    })}
                  >
                    {ay.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Gün Tipi Seçimi */}
            <div className="space-y-2">
              <Label>Gün Seçim Tipi</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={hatirlaticiForm.gun_tipi === 'belirli' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHatirlaticiForm({...hatirlaticiForm, gun_tipi: 'belirli'})}
                >
                  Belirli Günler
                </Button>
                <Button
                  type="button"
                  variant={hatirlaticiForm.gun_tipi === 'haftalik' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHatirlaticiForm({...hatirlaticiForm, gun_tipi: 'haftalik'})}
                >
                  Haftanın Günleri
                </Button>
              </div>
            </div>

            {/* Belirli Günler */}
            {hatirlaticiForm.gun_tipi === 'belirli' && (
              <div className="space-y-2">
                <Label>Hangi Günlerde? (Ayın kaçı)</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant={hatirlaticiForm.gunler.includes('ilk_gun') ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      gunler: toggleArrayValue(hatirlaticiForm.gunler, 'ilk_gun')
                    })}
                  >
                    İlk Gün
                  </Button>
                  <Button
                    type="button"
                    variant={hatirlaticiForm.gunler.includes('son_gun') ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHatirlaticiForm({
                      ...hatirlaticiForm, 
                      gunler: toggleArrayValue(hatirlaticiForm.gunler, 'son_gun')
                    })}
                  >
                    Son Gün
                  </Button>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((gun) => (
                    <Button
                      key={gun}
                      type="button"
                      variant={hatirlaticiForm.gunler.includes(gun) ? "default" : "outline"}
                      size="sm"
                      className="w-10"
                      onClick={() => setHatirlaticiForm({
                        ...hatirlaticiForm, 
                        gunler: toggleArrayValue(hatirlaticiForm.gunler, gun)
                      })}
                    >
                      {gun}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Haftalık Günler */}
            {hatirlaticiForm.gun_tipi === 'haftalik' && (
              <div className="space-y-2">
                <Label>Haftanın Hangi Günlerinde?</Label>
                <div className="flex gap-2 flex-wrap">
                  {haftaGunleri.map((gun) => (
                    <Button
                      key={gun.value}
                      type="button"
                      variant={hatirlaticiForm.hafta_gunleri.includes(gun.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setHatirlaticiForm({
                        ...hatirlaticiForm, 
                        hafta_gunleri: toggleArrayValue(hatirlaticiForm.hafta_gunleri, gun.value)
                      })}
                    >
                      {gun.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Belge Yükleme */}
            <div className="space-y-2">
              <Label>Belge Ekle (Opsiyonel)</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setHatirlaticiForm({
                        ...hatirlaticiForm,
                        belgeler: [...hatirlaticiForm.belgeler, { name: file.name, url: URL.createObjectURL(file) }]
                      });
                    }
                  }}
                  className="flex-1"
                />
              </div>
              {hatirlaticiForm.belgeler.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {hatirlaticiForm.belgeler.map((belge, index) => (
                    <Badge key={index} variant="secondary" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {belge.name}
                      <button
                        type="button"
                        onClick={() => setHatirlaticiForm({
                          ...hatirlaticiForm,
                          belgeler: hatirlaticiForm.belgeler.filter((_, i) => i !== index)
                        })}
                        className="ml-1 hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHatirlaticiModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleHatirlaticiSubmit}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-reminder-btn"
            >
              {editingHatirlatici ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hatırlatıcı Listesi Modalı */}
      <Dialog open={showHatirlaticiListModal} onOpenChange={setShowHatirlaticiListModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-purple-600" />
              Hatırlatıcı Listesi
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {hatirlaticilar.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Henüz hatırlatıcı eklenmemiş</p>
                <Button
                  onClick={() => {
                    setShowHatirlaticiListModal(false);
                    openHatirlaticiModal();
                  }}
                  className="mt-4 gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4" />
                  Hatırlatıcı Ekle
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {hatirlaticilar.map((hatirlatici) => (
                  <Card key={hatirlatici.id} className="overflow-hidden">
                    <div className="flex items-start gap-4 p-4">
                      <div 
                        className="w-3 h-full min-h-[60px] rounded-full flex-shrink-0"
                        style={{ backgroundColor: hatirlatici.renk || '#8B5CF6' }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{hatirlatici.baslik}</h3>
                            {hatirlatici.aciklama && (
                              <p className="text-sm text-gray-500 mt-1">{hatirlatici.aciklama}</p>
                            )}
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setShowHatirlaticiListModal(false);
                                openHatirlaticiModal(hatirlatici);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteHatirlatici(hatirlatici.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Badge variant="outline" className="text-xs">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {hatirlatici.yillar?.includes('her_yil') ? 'Her yıl' : hatirlatici.yillar?.join(', ')}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {hatirlatici.aylar?.includes('her_ay') 
                              ? 'Her ay' 
                              : hatirlatici.aylar?.map(a => aylar.find(ay => ay.value === a)?.label).filter(Boolean).join(', ')
                            }
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {hatirlatici.gun_tipi === 'haftalik' 
                              ? hatirlatici.hafta_gunleri?.map(g => haftaGunleri.find(h => h.value === g)?.label).filter(Boolean).join(', ')
                              : hatirlatici.gunler?.map(g => g === 'ilk_gun' ? '1' : g === 'son_gun' ? 'Son' : g).join(', ') + '. gün'
                            }
                          </Badge>
                          {!hatirlatici.aktif && (
                            <Badge variant="secondary" className="text-xs">Pasif</Badge>
                          )}
                        </div>
                        {hatirlatici.belgeler?.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {hatirlatici.belgeler.map((belge, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs gap-1">
                                <FileText className="h-3 w-3" />
                                {belge.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHatirlaticiListModal(false)}>
              Kapat
            </Button>
            <Button 
              onClick={() => {
                setShowHatirlaticiListModal(false);
                openHatirlaticiModal();
              }}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
            >
              <Plus className="h-4 w-4" />
              Yeni Hatırlatıcı
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tatil Ekle Modal */}
      <Dialog open={showHolidayModal} onOpenChange={setShowHolidayModal}>
        <DialogContent className="max-w-md" data-testid="holiday-modal">
          <DialogHeader>
            <DialogTitle>Resmi Tatil Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="holiday-name">Tatilin Adı</Label>
              <Input
                id="holiday-name"
                type="text"
                placeholder="örn. Ramazan Bayramı"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm((prev) => ({ ...prev, name: e.target.value }))}
                data-testid="holiday-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="holiday-start">Başlangıç</Label>
                <Input
                  id="holiday-start"
                  type="date"
                  value={holidayForm.startDate}
                  onChange={(e) => setHolidayForm((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                    endDate: (!prev.endDate || prev.endDate < e.target.value) ? e.target.value : prev.endDate,
                  }))}
                  data-testid="holiday-start-input"
                />
              </div>
              <div>
                <Label htmlFor="holiday-end">Bitiş</Label>
                <Input
                  id="holiday-end"
                  type="date"
                  min={holidayForm.startDate || undefined}
                  value={holidayForm.endDate}
                  onChange={(e) => setHolidayForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  data-testid="holiday-end-input"
                />
              </div>
            </div>

            {/* Seçilen günler için Tam Gün / Yarım Gün seçimi */}
            {getHolidayRangeDates().length > 0 && (
              <div className="border-t pt-3 space-y-2" data-testid="holiday-daytype-list">
                <Label className="text-sm text-gray-700">Gün tipleri</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {getHolidayRangeDates().map((d) => {
                    const dt = holidayDayTypes[d] || 'full';
                    const label = new Date(d + 'T00:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', weekday: 'short' });
                    return (
                      <div key={d} className="flex items-center justify-between gap-2" data-testid={`holiday-day-${d}`}>
                        <span className="text-sm text-gray-700 truncate">{label}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => setHolidayDayTypes((prev) => ({ ...prev, [d]: 'full' }))}
                            className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                              dt === 'full' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                            data-testid={`holiday-day-full-${d}`}
                          >
                            Tam Gün
                          </button>
                          <button
                            type="button"
                            onClick={() => setHolidayDayTypes((prev) => ({ ...prev, [d]: 'half' }))}
                            className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                              dt === 'half' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                            data-testid={`holiday-day-half-${d}`}
                          >
                            Yarım Gün
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Birden fazla gün için aralık seçebilirsiniz (örn. 3 günlük bayram). Yarım gün seçilen tatiller izin hesabında 0,5 gün olarak değerlendirilir.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHolidayModal(false)}>İptal</Button>
            <Button
              onClick={handleSaveHoliday}
              disabled={savingHoliday}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="holiday-save-btn"
            >
              {savingHoliday ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tatil Listesi Modal (görüntüle / düzenle / sil) */}
      <Dialog open={showHolidayListModal} onOpenChange={setShowHolidayListModal}>
        <DialogContent className="max-w-lg" data-testid="holiday-list-modal">
          <DialogHeader>
            <DialogTitle>Resmi Tatiller ({currentDate.getFullYear()})</DialogTitle>
            <DialogDescription>
              Eklenen tatilleri düzenleyebilir veya silebilirsiniz. Tatiller tüm kullanıcıların takviminde görünür.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto py-1">
            {holidays.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">Bu yıl için tatil eklenmemiş.</p>
            ) : (
              [...holidays].sort((a, b) => a.date.localeCompare(b.date)).map((h) => (
                <div key={h.id} className="flex items-center gap-2 border rounded-md p-2" data-testid={`holiday-row-${h.date}`}>
                  <div className="text-xs text-gray-500 w-24 shrink-0">
                    {new Date(h.date + 'T00:00:00').toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', weekday: 'short' })}
                  </div>
                  {editingHolidayId === h.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editHolidayData.name}
                        onChange={(e) => setEditHolidayData((p) => ({ ...p, name: e.target.value }))}
                        className="h-8 text-sm flex-1"
                        data-testid={`holiday-edit-name-${h.date}`}
                      />
                      <button
                        type="button"
                        onClick={() => setEditHolidayData((p) => ({ ...p, day_type: p.day_type === 'full' ? 'half' : 'full' }))}
                        className={`px-2 py-1 rounded text-xs font-medium border ${editHolidayData.day_type === 'half' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300'}`}
                        data-testid={`holiday-edit-daytype-${h.date}`}
                      >
                        {editHolidayData.day_type === 'half' ? 'Yarım' : 'Tam'}
                      </button>
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => saveEditHoliday(h)} data-testid={`holiday-edit-save-${h.date}`}>Kaydet</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingHolidayId(null)}>İptal</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-800 truncate">{h.name}</span>
                        {h.day_type === 'half' && <span className="ml-2 text-xs text-amber-600 font-medium">(Yarım Gün)</span>}
                      </div>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => startEditHoliday(h)} data-testid={`holiday-edit-${h.date}`}>Düzenle</Button>
                      <Button size="sm" variant="ghost" className="h-8 text-red-600 hover:bg-red-50" onClick={() => handleDeleteHoliday(h.id)} data-testid={`holiday-delete-row-${h.date}`}>Sil</Button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowHolidayListModal(false); setEditingHolidayId(null); }}>Kapat</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={() => { setShowHolidayListModal(false); openHolidayModal(); }} data-testid="holiday-list-add-btn">
              <CalendarIcon className="h-4 w-4 mr-1" /> Yeni Tatil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkCalendar;
